import React, {useEffect, useState} from "react";
import PropTypes from "prop-types";
import MagicMealPlanPreview from "./components/MagicMealPlanPreview";
import MagicMealPlanForm from "./components/MagicMealPlanForm";
import {Modal} from "bootstrap";
import {Redirect, useLocation} from "react-router-dom";
import Helpers from "../../services/Helpers";
import {useFeatureFlag} from "../../hooks/useFeatureFlag";
import {
  canAddCustomMealPlan,
  groupMagicMealPlans,
  isStarterPlanCopy,
  MAGIC_MEAL_PLANNER_STARTER_PLANS_FEATURE_FLAG,
} from "./magicMealPlanHelpers";

export default function MagicMealPlans(props) {
  const location = useLocation();
  const {isNotEmpty, pageTitle} = Helpers();
  const [idToDelete, setIdToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const starterPlansEnabled = useFeatureFlag(MAGIC_MEAL_PLANNER_STARTER_PLANS_FEATURE_FLAG);
  const isRecipeClubOnly = Boolean(props.memberStore.member?.hasRV) && !Boolean(props.memberStore.member?.hasMP);

  useEffect(() => {
    const element = document.getElementById("add-to-meal-plan-modal");
    if (isNotEmpty(element)) {
      const modal = Modal.getOrCreateInstance(element);
      modal.hide();
    }
  }, []);

  useEffect(() => {
    document.title = pageTitle(location.pathname);
  }, [location]);

  useEffect(() => {
    if (idToDelete !== null) {
      const element = document.getElementById("delete-modal");
      const resetModal = Modal.getOrCreateInstance(element);
      resetModal.show();
    }
  }, [idToDelete]);

  function confirmDelete() {
    if (idToDelete !== null) {
      setDeleting(true);
      props.mmpStore.deletePlan(idToDelete, () => {
        const element = document.getElementById("delete-modal");
        const resetModal = Modal.getOrCreateInstance(element);
        resetModal.hide();
        setDeleting(false);
        setIdToDelete(null);
      });
    }
  }

  function copyStarterPlan(planId, callback) {
    props.mmpStore.copyStarterPlan(planId, callback);
  }

  function restoreStarterPlan(planId) {
    props.mmpStore.restoreStarterPlan(planId);
  }

  function hideDeleteModal(event) {
    event.preventDefault();
    setIdToDelete(null);
    const element = document.getElementById("delete-modal");
    const resetModal = Modal.getOrCreateInstance(element);
    resetModal.hide();   
  }

  const hiddenStarterPlans = props.mmpStore.state.hiddenStarterPlans || [];
  const canCreateCustomPlan = canAddCustomMealPlan({
    plans: props.mmpStore.state.plans,
    hiddenPlans: hiddenStarterPlans,
    member: props.memberStore.member,
  }) && !isRecipeClubOnly;

  const newPlan = isRecipeClubOnly ? null : canCreateCustomPlan ? (
    <div className="row mp-new">
      <MagicMealPlanForm key="new" create={props.mmpStore.create}/>
    </div>
  ) : (
    <div className="row mp-new">
      <p>Hey there, plant-based boss. 🌱 You can have up to 20 custom Magic Meal Plans at one time. Each plan can include up to 14 sections, with 25 recipes per section. Please delete an existing custom plan to create a new one.</p>
    </div>
  );

  const {starterPlans, customPlans} = starterPlansEnabled
    ? groupMagicMealPlans(props.mmpStore.state.plans)
    : {starterPlans: [], customPlans: props.mmpStore.state.plans};
  const starterPlanCards = starterPlans.map(p => (
    <MagicMealPlanPreview key={p.id} mp={p} member={props.memberStore.member} mmpStore={props.mmpStore} onAddStarterPlan={copyStarterPlan} onProtectedAction={() => setShowUpgradeModal(true)} showDeleteModal={() => setIdToDelete(p.id)} />
  ));
  const customPlanCards = customPlans.map(p => (
    <MagicMealPlanPreview key={p.id} mp={p} member={props.memberStore.member} mmpStore={props.mmpStore} onAddStarterPlan={copyStarterPlan} onProtectedAction={() => setShowUpgradeModal(true)} showDeleteModal={() => setIdToDelete(p.id)} />
  ));
  const hiddenStarterPlanCards = hiddenStarterPlans.map(p => (
    <div key={p.id} className="tile col mb-4">
      <div className="card h-100 text-center border-0 shadow-sm bg-white">
        <div className="card-body p-3 bg-white rounded-bottom align-middle">
          <span className="badge rounded-pill text-bg-light border border-secondary-subtle text-dark mb-2">
            Hidden Starter Plan
          </span>
          <h2 className="card-title-1 py-1">{p.name}</h2>
          {p.starterPlanSourceName ? (
            <div className="small text-muted mb-2">Built from Starter Plan: {p.starterPlanSourceName}</div>
          ) : null}
          <button type="button" className="btn btn-primary mt-2" onClick={() => restoreStarterPlan(p.id)}>
            Restore
          </button>
        </div>
      </div>
    </div>
  ));
  
  function mmpToDeleteName() {
    let result = null;
    const mmpToDelete = props.mmpStore.state.plans.find(p => p.id === idToDelete);
    if (isNotEmpty(mmpToDelete)) {
      result = mmpToDelete.name;
    }
    return result;
  }

  function mmpToDelete() {
    return props.mmpStore.state.plans.find(p => p.id === idToDelete);
  }

  function deleteActionLabel() {
    return isStarterPlanCopy(mmpToDelete()) ? "hide" : "delete";
  }

  return props.authenticated ? (
    <div className="tile-collection main">
      <h1 className="text-center page-title mb-4 px-1">Magic Meal Planner</h1>
      <div className="container-lg">
        <div className="row h-100">
          <div className="col text">
            <div className="align-items-center text-center w-100 p-2">
              {newPlan}
            </div>
          </div>
          {starterPlans.length > 0 ? (
            <div className="mt-3">
              <h3 className="h-alt mt-2 text-center">Starter Meal Plans (Beta)</h3>
              <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 mp-saved">{starterPlanCards}</div>
            </div>
          ) : null}
          {customPlans.length > 0 ? (
            <div className="mt-3">
              <h3 className="h-alt mt-2 text-center">Your Custom Meal Plans</h3>
              <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 mp-saved">{customPlanCards}</div>
            </div>
          ) : null}
          {hiddenStarterPlans.length > 0 ? (
            <div className="mt-3">
              <h3 className="h-alt mt-2 text-center">Hidden Starter Meal Plans</h3>
              <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 mp-saved">{hiddenStarterPlanCards}</div>
            </div>
          ) : null}
        </div>
        <div id="delete-modal" data-bs-backdrop="static" data-bs-keyboard="false" className="modal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h1 className="modal-title h-alt">{isStarterPlanCopy(mmpToDelete()) ? "Hide Starter Plan" : "Delete Magic Meal Plan"}</h1>
                <button type="button" className="btn-close btn-close-black" onClick={hideDeleteModal} aria-label="Close"/>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to {deleteActionLabel()} <strong>{mmpToDeleteName()}</strong>?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-primary" onClick={hideDeleteModal}>No, cancel</button>
                {deleting ? (
                  <button type="button" className="btn btn-secondary me-auto" disabled={true}>{isStarterPlanCopy(mmpToDelete()) ? "Hiding..." : "Deleting..."}</button>
                ) : (
                  <button type="button" className="btn btn-secondary me-auto" onClick={confirmDelete}>Yes, {deleteActionLabel()}</button>
                )}
              </div>
            </div>
          </div>
        </div>
        {showUpgradeModal ? (
          <div className="modal d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h1 className="modal-title h-alt">Upgrade Required</h1>
                  <button type="button" className="btn-close btn-close-black" onClick={() => setShowUpgradeModal(false)} aria-label="Close"/>
                </div>
                <div className="modal-body">
                  <p>Upgrade to access the Magic Meal Planner.</p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowUpgradeModal(false)}>Close</button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  ) : <Redirect to="/sign-in"/>;
}

MagicMealPlans.propTypes = {
  memberStore: PropTypes.object,
  authenticated: PropTypes.bool,
  mmpStore: PropTypes.object,
}
